import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, MagnifyingGlass, ShieldCheck, Sparkle,
  UsersThree, Globe, Video, HouseSimple, CheckCircle, Star, Heart,
} from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import { WELLNESS_CATEGORIES } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WellnessLandingPage = ({ user }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(WELLNESS_CATEGORIES.map((c) => ({ ...c, professional_count: 0 })));
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/wellness/categories`).then((res) => {
      // merge counts into local WELLNESS_CATEGORIES (which has icons/colors)
      const counts = Object.fromEntries(res.data.map((c) => [c.id, c.professional_count]));
      setCategories(WELLNESS_CATEGORIES.map((c) => ({ ...c, professional_count: counts[c.id] || 0 })));
    }).catch(() => { /* silent — landing must render offline */ });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    navigate(`/wellness/find?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="wellness-landing">
      <Header user={user} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-20 md:pb-28 px-6" data-testid="wellness-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#34D399]/15 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-40 w-[420px] h-[420px] bg-[#1A4331]/10 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white border border-[#1A4331]/10 text-[#1A4331] px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] mb-6 shadow-sm"
          >
            <Sparkle size={13} weight="fill" className="text-[#C05C3D]" /> Your wellness, your way
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight text-[#1C1917] font-heading leading-[1.05] mb-6"
            data-testid="wellness-hero-heading"
          >
            Find the right expert<br />
            <span className="italic text-[#1A4331]">for your journey.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-base sm:text-lg leading-relaxed text-stone-600 max-w-2xl mx-auto mb-10"
          >
            Connect with verified nutritionists, trainers, yoga instructors, physiotherapists and wellness professionals — online or in person, anywhere in the world.
          </motion.p>

          {/* Search bar */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            onSubmit={handleSearch}
            className="max-w-2xl mx-auto mb-8"
          >
            <div className="flex items-center bg-white rounded-full shadow-xl shadow-stone-200/60 border border-stone-100 p-1.5 pl-5">
              <MagnifyingGlass size={20} weight="bold" className="text-stone-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="What do you need help with?"
                className="flex-1 px-4 py-3 outline-none text-[15px] bg-transparent"
                data-testid="wellness-search-input"
              />
              <button
                type="submit"
                className="bg-[#1A4331] hover:bg-[#2A5A44] text-white px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 shadow-md"
                data-testid="wellness-search-submit"
              >
                Search
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-4 text-sm text-stone-500">
              {['Yoga', 'Weight loss', 'Nutrition', 'Physiotherapy', 'Meditation'].map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => { setSearchQuery(chip); }}
                  className="px-3 py-1 rounded-full bg-white border border-stone-200 hover:border-[#1A4331] hover:text-[#1A4331] transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 justify-center items-center"
          >
            <button
              onClick={() => navigate('/wellness/find')}
              className="bg-[#1A4331] text-white px-8 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all shadow-xl shadow-[#1A4331]/20 flex items-center gap-2 hover:-translate-y-0.5 duration-200"
              data-testid="find-expert-cta"
            >
              Find a Wellness Expert <ArrowRight size={18} weight="bold" />
            </button>
            <button
              onClick={() => navigate('/wellness/join')}
              className="bg-white text-[#1A4331] border border-[#1A4331]/15 px-8 py-4 rounded-full font-medium hover:border-[#1A4331] transition-all flex items-center gap-2 hover:-translate-y-0.5 duration-200"
              data-testid="join-as-pro-cta"
            >
              Join as a Professional <ArrowUpRight size={18} weight="bold" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-y border-stone-100 py-8 px-6" data-testid="wellness-trust-strip">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, label: 'Nova Verified', desc: 'Every profile reviewed by our team' },
            { icon: CheckCircle, label: 'Credential Verified', desc: 'Real qualifications, no shortcuts' },
            { icon: Globe, label: 'Global Marketplace', desc: 'Online experts, worldwide' },
            { icon: Heart, label: 'Trusted by Nova', desc: 'From food to wellness — one home' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-[#F4F1EB] flex items-center justify-center flex-shrink-0">
                <item.icon size={22} weight="duotone" className="text-[#1A4331]" />
              </div>
              <div>
                <p className="font-semibold text-[#1C1917] text-sm">{item.label}</p>
                <p className="text-xs text-stone-500 leading-relaxed mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 md:py-28 px-6" data-testid="wellness-categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">Explore Categories</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
              Care that fits <span className="italic text-[#1A4331]">every goal.</span>
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              Whatever your journey looks like — losing weight, improving mobility, sleeping better — start with a category that speaks to you.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat, idx) => (
              <motion.button
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                onClick={() => navigate(`/wellness/find?category=${cat.id}`)}
                className={`relative text-left bg-gradient-to-br ${cat.tint} rounded-3xl p-7 border border-white overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
                data-testid={`wellness-category-${cat.id}`}
              >
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/50 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-5">
                    <cat.icon size={26} weight="duotone" style={{ color: cat.color }} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1C1917] mb-1 font-heading tracking-tight">{cat.name}</h3>
                  <p className="text-sm text-stone-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: cat.tagline }} />
                  <div className="flex items-center justify-between pt-4 border-t border-white/60">
                    <span className={`text-xs font-bold uppercase tracking-[0.14em] ${cat.accent}`}>
                      {cat.professional_count === 0 ? 'Opening soon' : `${cat.professional_count} experts`}
                    </span>
                    <ArrowUpRight size={18} weight="bold" className="text-stone-400 group-hover:text-[#1A4331] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 px-6 bg-gradient-to-b from-transparent to-white" data-testid="wellness-how-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-xl mx-auto mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">How Nova Wellness Works</p>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading">
              From doubt to done in <span className="italic text-[#1A4331]">3 steps.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: '01', title: 'Discover', desc: 'Browse verified professionals by category, mode, and location. Filter for what matters to you.', icon: MagnifyingGlass },
              { n: '02', title: 'Book', desc: 'Pick a slot that works. Instant booking or send a consultation request — you decide.', icon: CheckCircle },
              { n: '03', title: 'Transform', desc: 'Meet online or in person, follow a monthly program, and track your progress in one place.', icon: Sparkle },
            ].map((step, idx) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-3xl p-8 border border-stone-100 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#1A4331] text-white flex items-center justify-center">
                    <step.icon size={22} weight="duotone" />
                  </div>
                  <span className="text-xs font-bold tracking-[0.14em] text-stone-300">{step.n}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#1C1917] mb-2 font-heading tracking-tight">{step.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MODE STRIP */}
      <section className="py-16 md:py-20 px-6" data-testid="wellness-mode-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Video, label: 'Online', desc: 'Video, audio & chat with experts globally' },
              { icon: UsersThree, label: 'In-person', desc: 'Meet at a nearby clinic or studio' },
              { icon: HouseSimple, label: 'Home Visit', desc: 'Have a professional come to you' },
            ].map((m) => (
              <div key={m.label} className="bg-white rounded-3xl p-6 border border-stone-100 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#F0F7F1] flex items-center justify-center flex-shrink-0">
                  <m.icon size={26} weight="duotone" className="text-[#1A4331]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1917]">{m.label}</p>
                  <p className="text-sm text-stone-500 leading-snug">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROFESSIONAL CTA */}
      <section className="py-20 md:py-28 px-6" data-testid="wellness-pro-cta-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-[#0F2A20] via-[#1A4331] to-[#2A5A44] text-white rounded-[2.5rem] p-8 md:p-14 overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 bg-[#34D399]/25 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 bg-[#C05C3D]/20 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-[#34D399] px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] mb-5">
                  <Sparkle size={13} weight="fill" /> For Professionals
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold font-heading tracking-tight mb-5 leading-[1.1]">
                  Grow your practice <span className="italic text-[#34D399]">with Nova.</span>
                </h2>
                <p className="text-stone-200 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
                  Create your profile for free. Reach new clients online or in your city. Get paid securely. Registration is <span className="font-semibold text-white">always free</span>.
                </p>
                <button
                  onClick={() => navigate('/wellness/join')}
                  className="bg-[#34D399] text-[#1A4331] px-8 py-4 rounded-full font-bold hover:bg-white transition-all shadow-xl shadow-black/20 flex items-center gap-2"
                  data-testid="wellness-pro-cta-btn"
                >
                  Join as a Professional <ArrowRight size={18} weight="bold" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { title: 'Free', desc: 'Registration' },
                  { title: '90%', desc: 'You keep' },
                  { title: 'Global', desc: 'Reach' },
                  { title: 'Verified', desc: 'Trust' },
                ].map((b) => (
                  <div key={b.title} className="bg-white/8 backdrop-blur border border-white/10 rounded-2xl p-5">
                    <p className="text-3xl md:text-4xl font-semibold text-white font-heading tracking-tight">{b.title}</p>
                    <p className="text-sm text-stone-300 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default WellnessLandingPage;
